/** Brand-voiced formatting helpers — kept here so do/don't is
 * applied consistently. */

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function formatCents(cents: number | null): string {
  if (cents === null || cents === undefined) return "—";
  if (cents < 100) return `${cents}¢`;
  return `$${(cents / 100).toFixed(2)}`;
}

/** ISO timestamp → "Apr 25, 11:47 PM" (local TZ). */
export function formatTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Short ID for display (last 8 chars of UUID). */
export function shortId(id: string): string {
  return id.slice(-8);
}

/** ISO timestamp → relative phrase like "2 days ago" / "just now"
 *  for recent events, or the absolute date for anything older than
 *  a week. The Quote page shows a lot of "sent ... viewed ...
 *  signed ..." dates and absolute timestamps clutter the page;
 *  the relative phrasing matches how the contractor thinks about
 *  the project ("we sent that yesterday"). */
export function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const target = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - target.getTime();
  const diffSec = Math.round(diffMs / 1000);

  if (diffSec < 0) {
    // Future date — show absolute (e.g. "Valid through May 15").
    return target.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: target.getFullYear() === now.getFullYear() ? undefined : "numeric",
    });
  }
  if (diffSec < 45) return "just now";
  if (diffSec < 90) return "1 minute ago";

  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 45) return `${diffMin} minutes ago`;
  if (diffMin < 90) return "1 hour ago";

  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hours ago`;
  if (diffHr < 36) return "yesterday";

  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay} days ago`;

  // Anything older: absolute date, dropping the year unless not
  // current.
  return target.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: target.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
}
